import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Autocomplete,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Chip,
} from '@mui/material';
import { RequestQuote as ARIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, doc, updateDoc, where, onSnapshot, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { applyMilestoneTransition } from '../utils/milestones';
import { getClientAvailableBalance } from '../utils/clientBalanceService';
import { useConfig } from '../contexts/ConfigContext';
import { normalizeFrequency } from '../utils/financials';
import { createAutoClientDeposit } from '../utils/bankService';
import { fallbackColorForUid } from '../utils/userColors';
import { AR_COMMUNICATION_ADMIN_ONLY } from '../utils/statusConstants';
import ARCommunicationCell from './ARCommunicationCell';

const isNextMonthReceivableTiming = (value) => value === 'next_month';

const parseExpectedDateParts = (expectedDate) => {
  const value = String(expectedDate || '').trim();
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = match[3] ? Number(match[3]) : 1;
  if (!Number.isInteger(year) || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
};

const getScheduledReceivableDate = (expectedDate, scheduledReceivableTiming) => {
  const parts = parseExpectedDateParts(expectedDate);
  if (!parts) return null;
  const monthOffset = isNextMonthReceivableTiming(scheduledReceivableTiming) ? 1 : 0;
  return new Date(parts.year, parts.month - 1 + monthOffset, monthOffset ? 1 : parts.day);
};

const getTodayDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const getReceivableRowKey = ({ contractId, lineId }) => (
  `${String(contractId || '')}::${String(lineId ?? '')}`
);

// ar_communications doc id for an AR line (Firestore ids can't contain '/')
const getCommunicationDocId = (line) => getReceivableRowKey(line).replace(/\//g, '_');

export default function AccountsReceivable() {
  const { isSuperAdmin, currentUser, userProfile } = useAuth();
  const { getFinanceConfig } = useConfig();
  const financeConfig = getFinanceConfig ? getFinanceConfig() : {};
  const creditCardProcessingPercent = Math.max(0, Math.min(100, Number(financeConfig?.creditCardProcessingPercent || 0)));
  const [loading, setLoading] = useState(true);
  // Communication status per AR line, keyed by ar_communications doc id
  const [communications, setCommunications] = useState({});
  const [baseLoaded, setBaseLoaded] = useState(false);
  const [contractsLoaded, setContractsLoaded] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [entities, setEntities] = useState([]);
  const [filters, setFilters] = useState({ clientId: '', contractId: '', status: '', overdue: '' });
  const [selectedClientIndex, setSelectedClientIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [clientBalances, setClientBalances] = useState({});
  const [invoiceEdits, setInvoiceEdits] = useState({});
  const [appliedDrafts, setAppliedDrafts] = useState({});
  const [depositingRowKey, setDepositingRowKey] = useState('');

  const ucenaCompany = useMemo(() => entities.find((entity) => (
    entity?.entityType === 'company'
    && String(entity?.status || 'active').toLowerCase() === 'active'
    && /\bucena\b/i.test(String(entity?.entityName || ''))
  )) || null, [entities]);

  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));

  // Sum of currently applied amounts per client across all outstanding (Invoiced/Partial) lines
  const appliedTotalsByClient = useMemo(() => {
    const map = {};
    contracts.forEach((ct) => {
      const clientId = ct.clientEntityId;
      if (!clientId) return;
      const logs = Array.isArray(ct.milestoneLogs) ? ct.milestoneLogs : [];
      logs.forEach((m) => {
        const lineStatus = String(m.status || '');
        if (lineStatus === 'Invoiced' || lineStatus === 'Partial') {
          const applied = Number(m.appliedAmount || 0);
          map[clientId] = (map[clientId] || 0) + applied;
        } else if (lineStatus === 'Paid' && m.clientBalanceTracked === true) {
          const expected = Number(m.expectedAmount || 0);
          const actual = Number(m.actualAmount || 0);
          const paidBase = actual > 0 ? Math.min(actual, expected) : expected;
          map[clientId] = (map[clientId] || 0) + paidBase;
        }
      });
    });
    return map;
  }, [contracts]);

  // Effective available per client = ledger available - sum of applied across open lines
  const effectiveClientBalances = useMemo(() => {
    const res = {};
    Object.keys(clientBalances || {}).forEach((id) => {
      const base = Number(clientBalances[id] || 0);
      const applied = Number(appliedTotalsByClient[id] || 0);
      res[id] = Math.max(0, Number((base - applied).toFixed(2)));
    });
    return res;
  }, [clientBalances, appliedTotalsByClient]);

  const getEffectiveAvailable = (clientId) => {
    const base = Number(clientBalances[clientId] || 0);
    let deduction = 0;
    const deductionDetails = [];
    contracts.forEach((ct) => {
      if (ct.clientEntityId !== clientId) return;
      const logs = Array.isArray(ct.milestoneLogs) ? ct.milestoneLogs : [];
      logs.forEach((m) => {
        const lineStatus = String(m.status || '');
        if (lineStatus === 'Invoiced' || lineStatus === 'Partial') {
          const applied = Number(m.appliedAmount || 0);
          deduction += applied;
          deductionDetails.push({ contractId: ct.id, milestoneId: m.id, status: lineStatus, amount: applied });
        } else if (lineStatus === 'Paid' && m.clientBalanceTracked === true) {
          const expected = Number(m.expectedAmount || 0);
          const actual = Number(m.actualAmount || 0);
          const paidBase = actual > 0 ? Math.min(actual, expected) : expected;
          deduction += paidBase;
          deductionDetails.push({ contractId: ct.id, milestoneId: m.id, status: lineStatus, amount: paidBase });
        }
      });
    });
    const result = Math.max(0, Number((base - deduction).toFixed(2)));
    
    // 🔍 RACE CONDITION LOG: Track available balance calculation
    console.log('[AR][getEffectiveAvailable] CALCULATED:', {
      timestamp: new Date().toISOString(),
      clientId,
      base,
      deduction,
      result,
      deductionCount: deductionDetails.length,
      deductionDetails
    });
    
    return result;
  };

  const getClientAvailableDisplay = (clientId) => {
    const base = Number(clientBalances[clientId] || 0);
    let deduction = 0;
    contracts.forEach((ct) => {
      if (ct.clientEntityId !== clientId) return;
      const logs = Array.isArray(ct.milestoneLogs) ? ct.milestoneLogs : [];
      logs.forEach((m) => {
        const lineStatus = String(m.status || '');
        if (lineStatus === 'Invoiced' || lineStatus === 'Partial') {
          const draftVal = appliedDrafts[getReceivableRowKey({ contractId: ct.id, lineId: m.id })];
          const applied = draftVal !== undefined && draftVal !== '' ? Number(draftVal || 0) : Number(m.appliedAmount || 0);
          deduction += applied;
        } else if (lineStatus === 'Paid' && m.clientBalanceTracked === true) {
          const expected = Number(m.expectedAmount || 0);
          const actual = Number(m.actualAmount || 0);
          const paidBase = actual > 0 ? Math.min(actual, expected) : expected;
          deduction += paidBase;
        }
      });
    });
    return Math.max(0, Number((base - deduction).toFixed(2)));
  };

  const loadBase = async () => {
    setBaseLoaded(false);
    try {
      const [clientsSnap, entitiesSnap] = await Promise.all([
        getDocs(query(collection(db, 'clients'), orderBy('clientName', 'asc'))),
        getDocs(query(collection(db, 'entities'), orderBy('entityName', 'asc'))),
      ]);
      const cls = clientsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const ents = entitiesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setClients(cls);
      setEntities(ents);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load AR data', severity: 'error' });
    } finally {
      setBaseLoaded(true);
    }
  };

  useEffect(() => { loadBase(); }, []);

  useEffect(() => {
    const qContracts = query(collection(db, 'contracts'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      qContracts,
      (snap) => {
        const cts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const activeContracts = cts.filter((c) => ['Active', 'Inactive'].includes(String(c.status || '')));
        setContracts(activeContracts);
        setContractsLoaded(true);
      },
      () => {
        setSnackbar({ open: true, message: 'Failed to load contracts', severity: 'error' });
        setContractsLoaded(true);
      }
    );
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, []);

  useEffect(() => {
    setLoading(!(baseLoaded && contractsLoaded));
  }, [baseLoaded, contractsLoaded]);

  const getClientName = (clientId) => clients.find((c) => c.id === clientId)?.clientName || 'Unknown Client';
  

  const lineRows = useMemo(() => {
    const rows = [];
    contracts.forEach((ct) => {
      const logs = Array.isArray(ct.milestoneLogs) ? ct.milestoneLogs : [];
      logs.forEach((m) => {
        const lineStatus = String(m.status || '');
        const today = getTodayDate();
        const receivableDate = getScheduledReceivableDate(m.expectedDate, ct.scheduledReceivableTiming);
        // include Invoiced and Partial; also include Scheduled whose receivable timing has arrived or passed
        const hasReceivableDateArrived = receivableDate ? receivableDate <= today : false;
        const include = (lineStatus === 'Invoiced' || lineStatus === 'Partial') || (lineStatus === 'Scheduled' && hasReceivableDateArrived);
        if (!include) return;
        // Derive expected with processing for Scheduled lines if not already present
        const baseComponents = (
          Number(m.expectedRevenueBase || 0) +
          Number(m.expectedTransport || 0) +
          Number(m.expectedCommission || 0)
        );
        let expected = Number(m.expectedAmount || 0);
        if (String(m.status || '') === 'Scheduled') {
          if (typeof m.expectedProcessingFee === 'number') {
            expected = Number(m.expectedAmount || (baseComponents + Number(m.expectedProcessingFee || 0)));
          } else if (baseComponents > 0) {
            // Use method-aware percent: Credit Card -> cc %, Check -> check %, Transfer/other -> 0
            const method = String(ct?.defaultPaymentMethod || '').toLowerCase();
            const processingPercent = method === 'credit card' || method === 'creditcard' || method === 'credit_card'
              ? creditCardProcessingPercent
              : (method === 'check' ? (Number(getFinanceConfig()?.checkProcessingPercent || 0)) : 0);
            const fee = baseComponents * (processingPercent / 100);
            expected = baseComponents + fee;
          }
        }
        const actual = Number(m.actualAmount || 0);
        const base = lineStatus === 'Partial' ? (actual > 0 ? actual : expected) : expected;
        const applied = Number(m.appliedAmount || 0);
        const outstanding = Math.max(0, base - applied);
        // Keep all Invoiced lines visible even if fully applied; hide only fully applied Partial lines
        if (lineStatus === 'Partial' && outstanding <= 0) return;
        const overdue = receivableDate ? receivableDate < today : false;
        // derive payment method code for display (CC/CK/TR)
        const methodStr = String(ct?.defaultPaymentMethod || '').toLowerCase();
        let methodCode = 'TR';
        if (methodStr === 'credit card' || methodStr === 'creditcard' || methodStr === 'credit_card') methodCode = 'CC';
        else if (methodStr === 'check') methodCode = 'CK';
        rows.push({
          clientId: ct.clientEntityId,
          clientName: getClientName(ct.clientEntityId),
          contractId: ct.id,
          contractTitle: ct.title || 'Contract',
          beneficiaryCompanyId: ct.beneficiaryCompanyId || null,
          partnerRoles: Array.isArray(ct.partnerRoles) ? ct.partnerRoles : [],
          lineId: m.id,
          expectedDate: m.expectedDate || '',
          status: lineStatus,
          invoiceId: m.invoiceId || '',
          invoiceUrl: m.invoiceUrl || '',
          expectedAmount: expected,
          actualAmount: actual,
          appliedAmount: applied,
          outstanding,
          overdue,
          paymentMethodCode: methodCode,
          paymentMethod: ct?.defaultPaymentMethod || 'Transfer',
          autoDepositBankLineId: m.autoDepositBankLineId || '',
        });
      });
    });
    // filters
    const byClient = rows.filter((r) => (filters.clientId ? r.clientId === filters.clientId : true));
    const byStatus = byClient.filter((r) => (filters.status ? r.status === filters.status : true));
    const byOverdue = byStatus.filter((r) => (filters.overdue === 'yes' ? r.overdue : filters.overdue === 'no' ? !r.overdue : true));
    const byContract = byOverdue.filter((r) => (filters.contractId ? r.contractId === filters.contractId : true));
    return byContract;
  }, [contracts, clients, entities, filters]);

  const groupedByClient = useMemo(() => {
    const map = new Map();
    lineRows.forEach((r) => {
      if (!map.has(r.clientId)) map.set(r.clientId, { clientId: r.clientId, clientName: r.clientName, lines: [], outstanding: 0 });
      const bucket = map.get(r.clientId);
      bucket.lines.push(r);
      bucket.outstanding += r.outstanding;
    });
    return Array.from(map.values()).sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [lineRows]);

  useEffect(() => {
    if (selectedClientIndex >= groupedByClient.length) {
      setSelectedClientIndex(0);
    }
  }, [groupedByClient, selectedClientIndex]);

  // Load available balances for displayed clients
  useEffect(() => {
    // unsubscribe previous listeners
    const unsubs = [];
    try {
      const ids = Array.from(new Set(groupedByClient.map((g) => g.clientId).filter(Boolean)));
      if (ids.length === 0) {
        setClientBalances({});
        return () => {};
      }
      const balances = {};
      ids.forEach((id) => {
        const qcb = query(collection(db, 'client_balance_entries'), where('clientId', '==', id));
        const unsub = onSnapshot(qcb, (snap) => {
          let sum = 0;
          snap.forEach((d) => { sum += Number(d.data()?.amountUsd || 0); });
          balances[id] = sum;
          setClientBalances((prev) => ({ ...prev, [id]: sum }));
        });
        unsubs.push(unsub);
      });
    } catch {}
    return () => { unsubs.forEach((u) => { if (typeof u === 'function') u(); }); };
  }, [groupedByClient]);

  

  const updateAppliedAmount = async (line, amount) => {
    try {
      if (!isSuperAdmin()) { setSnackbar({ open: true, message: 'Read-only for Partners', severity: 'info' }); return; }
      const ct = contracts.find((c) => c.id === line.contractId);
      const logs = Array.isArray(ct?.milestoneLogs) ? ct.milestoneLogs.map((m) => ({ ...m })) : [];
      const idx = logs.findIndex((m) => m.id === line.lineId);
      if (idx < 0) return;
      const expected = Number(logs[idx].expectedAmount || 0);
      const actual = Number(logs[idx].actualAmount || 0);
      const base = logs[idx].status === 'Partial' ? (actual > 0 ? actual : expected) : expected;
      const normalized = Math.max(0, Number(amount || 0));
      // include current line's applied back into the effective available ceiling
      const currentApplied = Number(logs[idx].appliedAmount || 0);
      const clientEffective = Number(getEffectiveAvailable(line.clientId) || 0) + currentApplied;
      const allowedMax = Math.min(base, clientEffective);
      const finalApplied = normalized > allowedMax + 1e-6 ? Number(allowedMax.toFixed(2)) : Number(normalized.toFixed(2));
      
      // 🔍 RACE CONDITION LOG: Track applied amount updates
      console.log('[AR][updateAppliedAmount] BEFORE UPDATE:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: line.lineId,
        status: logs[idx].status,
        requestedAmount: amount,
        currentApplied,
        normalized,
        base,
        clientEffective,
        allowedMax,
        finalApplied,
        wasCapped: finalApplied !== normalized,
        delta: finalApplied - currentApplied
      });
      
      // optimistic local update to reflect immediately in Outstanding and Available
      const updatedContracts = contracts.map((c) => {
        if (c.id !== ct.id) return c;
        const newLogs = (Array.isArray(c.milestoneLogs) ? c.milestoneLogs.map((m) => ({ ...m })) : []);
        const i = newLogs.findIndex((m) => m.id === line.lineId);
        if (i >= 0) {
          newLogs[i].appliedAmount = finalApplied;
          newLogs[i].clientBalanceTracked = finalApplied > 0;
        }
        return { ...c, milestoneLogs: newLogs };
      });
      setContracts(updatedContracts);

      // persist to Firestore
      logs[idx].appliedAmount = finalApplied;
      logs[idx].clientBalanceTracked = finalApplied > 0;
      await updateDoc(doc(db, 'contracts', ct.id), { milestoneLogs: logs });
      
      console.log('[AR][updateAppliedAmount] AFTER FIRESTORE UPDATE:', {
        timestamp: new Date().toISOString(),
        contractId: ct.id,
        milestoneId: line.lineId,
        persistedApplied: finalApplied,
        success: true
      });
      
      setSnackbar({ open: true, message: finalApplied !== normalized ? 'Applied capped to available balance/line max' : 'Applied amount updated', severity: finalApplied !== normalized ? 'info' : 'success' });
      const rowKey = getReceivableRowKey(line);
      setAppliedDrafts((prev)=>{ const { [rowKey]: _removed, ...rest } = prev; return rest; });
    } catch (e) {
      console.error('[AR][updateAppliedAmount] FAILED:', {
        timestamp: new Date().toISOString(),
        error: e.message,
        stack: e.stack
      });
      setSnackbar({ open: true, message: 'Failed to update applied amount', severity: 'error' });
    }
  };

  const handleMarkPaid = async (rows) => {
    try {
      if (!isSuperAdmin()) { setSnackbar({ open: true, message: 'Read-only for Partners', severity: 'info' }); return; }
      for (const l of rows) {
        const ct = contracts.find((c) => c.id === l.contractId);
        const logs = Array.isArray(ct?.milestoneLogs) ? ct.milestoneLogs.map((m) => ({ ...m })) : [];
        const idx = logs.findIndex((m) => m.id === l.lineId);
        if (idx < 0) continue;
        const m = logs[idx];
        // Determine processing fee based on contract default payment method
        const method = String(ct?.defaultPaymentMethod || '').toLowerCase();
        const processingPercent = method === 'credit card' || method === 'creditcard' || method === 'credit_card'
          ? creditCardProcessingPercent
          : (method === 'check' ? (Number(getFinanceConfig()?.checkProcessingPercent || 0)) : 0);
        const expectedBeforeProcessing = (
          Number(m.expectedRevenueBase || 0) +
          Number(m.expectedTransport || 0) +
          Number(m.expectedCommission || 0)
        );
        let expected = Number(m.expectedAmount || 0);
        if (String(m.status || '') === 'Scheduled') {
          if (typeof m.expectedProcessingFee === 'number') {
            expected = Number(m.expectedAmount || (expectedBeforeProcessing + Number(m.expectedProcessingFee || 0)));
          } else if (expectedBeforeProcessing > 0) {
            const fee = expectedBeforeProcessing * (processingPercent / 100);
            expected = expectedBeforeProcessing + fee;
          }
        }
        const actual = Number(m.actualAmount || 0);
        const base = (actual > 0 ? Math.min(actual, expected) : expected);
        const applied = Number(m.appliedAmount || 0);
        
        // 🔍 RACE CONDITION LOG: Track Paid transition initialization
        console.log('[AR][handleMarkPaid] INITIATING PAID TRANSITION:', {
          timestamp: new Date().toISOString(),
          contractId: ct.id,
          milestoneId: m.id,
          status: m.status,
          expected,
          actual,
          base,
          appliedBefore: applied,
          clientAvailable: getEffectiveAvailable(l.clientId),
          allMilestonesApplied: logs.map(ml => ({ id: ml.id, status: ml.status, applied: ml.appliedAmount }))
        });
        
        if (applied + 1e-6 < base) { setSnackbar({ open: true, message: 'Cannot mark Paid: applied is less than amount', severity: 'warning' }); return; }
        // Normalize applied to invoice total before transition (align with Contracts.js)
        const appliedNormalized = Math.round((applied - base) * 100) !== 0;
        if (appliedNormalized) {
          console.log('[AR][handleMarkPaid] NORMALIZING APPLIED AMOUNT:', {
            timestamp: new Date().toISOString(),
            contractId: ct.id,
            milestoneId: m.id,
            appliedBefore: applied,
            base,
            appliedAfter: base,
            delta: base - applied,
            roundedDelta: Math.round((applied - base) * 100)
          });
          logs[idx].appliedAmount = base;
        }
        const ctForApply = { ...ct, milestoneLogs: logs };
        await applyMilestoneTransition({
          contract: ctForApply,
          milestoneId: m.id,
          nextStatus: 'Paid',
          entities,
          financeConfig,
          createdBy: currentUser?.uid || 'system',
          getEffectiveClientAvailable: (clientId) => {
            const available = Number(getEffectiveAvailable(clientId) || 0);
            console.log('[AR][handleMarkPaid] getEffectiveClientAvailable CALLED:', {
              timestamp: new Date().toISOString(),
              clientId,
              available,
              calledFrom: 'handleMarkPaid'
            });
            return available;
          },
          persistUpdate: async (updatedContract) => {
            console.log('[AR][handleMarkPaid] PERSISTING PAID MILESTONE:', {
              timestamp: new Date().toISOString(),
              contractId: updatedContract.id,
              milestoneId: m.id,
              postingRefs: updatedContract.milestoneLogs.find(ml => ml.id === m.id)?.postingRefs
            });
            await updateDoc(doc(db, 'contracts', updatedContract.id), { milestoneLogs: updatedContract.milestoneLogs });
          },
        });
      }
      setSnackbar({ open: true, message: 'Marked as Paid', severity: 'success' });
      await loadBase();
    } catch (e) {
      console.error('[AR][handleMarkPaid] FAILED:', {
        timestamp: new Date().toISOString(),
        error: e.message,
        stack: e.stack
      });
      setSnackbar({ open: true, message: 'Failed to mark Paid', severity: 'error' });
    }
  };

  const handleStatusChange = async (line, newStatus) => {
    if (newStatus === line.status) return;
    if (newStatus === 'Paid') {
      // Validate applied covers the amount; do not enforce client available for Paid
      try {
        const ct = contracts.find((c) => c.id === line.contractId);
        if (!ct) return;
        const logs = Array.isArray(ct?.milestoneLogs) ? ct.milestoneLogs : [];
        const idx = logs.findIndex((m) => m.id === line.lineId);
        if (idx < 0) return;
        const m = logs[idx];
        const expected = Number(m.expectedAmount || 0);
        const actual = Number(m.actualAmount || 0);
        const base = (actual > 0 ? Math.min(actual, expected) : expected);
        const applied = Number(m.appliedAmount || 0);
        if (applied + 1e-6 < base) { setSnackbar({ open: true, message: 'Cannot mark Paid: applied is less than amount', severity: 'warning' }); return; }
      } catch {}
      await handleMarkPaid([line]);
      return;
    }
    if (newStatus === 'Partial') {
      // Partial requires applied within available and less than expected
      try {
        if (!isSuperAdmin()) { setSnackbar({ open: true, message: 'Read-only for Partners', severity: 'info' }); return; }
        const ct = contracts.find((c) => c.id === line.contractId);
        if (!ct) return;
        const logs = Array.isArray(ct?.milestoneLogs) ? ct.milestoneLogs.map((m) => ({ ...m })) : [];
        const idx = logs.findIndex((m) => m.id === line.lineId);
        if (idx < 0) return;
        const m = logs[idx];
        const expected = Number(m.expectedAmount || 0);
        const applied = Number(m.appliedAmount || 0);
        const available = Number(getEffectiveAvailable(ct.clientEntityId) || 0);
        if (!(applied > 0 && applied < expected)) { setSnackbar({ open: true, message: 'Partial requires applied > 0 and < invoice total', severity: 'warning' }); return; }
        if (available + 1e-6 < applied) { setSnackbar({ open: true, message: 'Insufficient client available balance for Partial', severity: 'warning' }); return; }
        await applyMilestoneTransition({
          contract: ct,
          milestoneId: m.id,
          nextStatus: 'Partial',
          entities,
          financeConfig,
          createdBy: currentUser?.uid || 'system',
          getEffectiveClientAvailable: (clientId) => Number(getEffectiveAvailable(clientId) || 0),
          persistUpdate: async (updatedContract) => {
            await updateDoc(doc(db, 'contracts', updatedContract.id), { milestoneLogs: updatedContract.milestoneLogs });
          },
        });
        setSnackbar({ open: true, message: 'Marked Partial', severity: 'success' });
        await loadBase();
        return;
      } catch (e) {
        setSnackbar({ open: true, message: 'Failed to mark Partial', severity: 'error' });
        return;
      }
    }
    if (newStatus === 'Invoiced') {
      try {
        if (!isSuperAdmin()) { setSnackbar({ open: true, message: 'Read-only for Partners', severity: 'info' }); return; }
        const ct = contracts.find((c) => c.id === line.contractId);
        if (!ct) return;
        const logs = Array.isArray(ct?.milestoneLogs) ? ct.milestoneLogs.map((m) => ({ ...m })) : [];
        const idx = logs.findIndex((m) => m.id === line.lineId);
        if (idx < 0) return;

        // If moving Scheduled -> Invoiced, ensure expectedAmount includes processing fee and persist it
        if (String(logs[idx].status || '') === 'Scheduled') {
          const baseComponents = (
            Number(logs[idx].expectedRevenueBase || 0) +
            Number(logs[idx].expectedTransport || 0) +
            Number(logs[idx].expectedCommission || 0)
          );
          if (typeof logs[idx].expectedProcessingFee !== 'number' && baseComponents > 0) {
            const method = String(ct?.defaultPaymentMethod || '').toLowerCase();
            const processingPercent = method === 'credit card' || method === 'creditcard' || method === 'credit_card'
              ? creditCardProcessingPercent
              : (method === 'check' ? (Number(getFinanceConfig()?.checkProcessingPercent || 0)) : 0);
            const fee = baseComponents * (processingPercent / 100);
            logs[idx].expectedProcessingFee = Number(fee.toFixed(2));
            logs[idx].expectedAmount = Number((baseComponents + fee).toFixed(2));
          }
        }

        // Important: pass the updated logs into the transition, otherwise the transition may operate on stale milestoneLogs
        const ctForApply = { ...ct, milestoneLogs: logs };
        await applyMilestoneTransition({
          contract: ctForApply,
          milestoneId: logs[idx].id,
          nextStatus: 'Invoiced',
          entities,
          financeConfig,
          createdBy: currentUser?.uid || 'system',
          getEffectiveClientAvailable: (clientId) => Number(getEffectiveAvailable(clientId) || 0),
          persistUpdate: async (updatedContract) => {
            // Mark one-time services as applied to prevent duplicate vendor credits on future milestones
            // Store the milestone ID so we know which milestone to post margins for
            const services = Array.isArray(ct.services) ? ct.services : [];
            const currentMilestoneId = logs[idx].id;
            const hasUnappliedOneTime = services.some(
              (s) => normalizeFrequency(s?.frequency) === 'one-time' && !s?.oneTimeApplied
            );
            let servicesUpdate = null;
            if (hasUnappliedOneTime) {
              servicesUpdate = services.map((s) => {
                if (normalizeFrequency(s?.frequency) === 'one-time' && !s?.oneTimeApplied) {
                  return { ...s, oneTimeApplied: true, oneTimeAppliedMilestoneId: currentMilestoneId };
                }
                return s;
              });
            }
            const update = servicesUpdate
              ? { milestoneLogs: updatedContract.milestoneLogs, services: servicesUpdate }
              : { milestoneLogs: updatedContract.milestoneLogs };
            await updateDoc(doc(db, 'contracts', updatedContract.id), update);
          },
        });
        setSnackbar({ open: true, message: 'Marked Invoiced and vendor credits posted', severity: 'success' });
        await loadBase();
      } catch (e) {
        const detail = String(e?.message || '').trim();
        const message = detail ? `Failed to mark Invoiced: ${detail}` : 'Failed to mark Invoiced';
        setSnackbar({ open: true, message, severity: 'error' });
      }
    }
  };

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'ar_communications'),
      (snap) => {
        const byId = {};
        snap.forEach((d) => { byId[d.id] = d.data(); });
        setCommunications(byId);
      },
      (error) => console.error('Error loading AR communications:', error)
    );
    return () => unsub();
  }, []);

  const updateCommunicationStatus = async (line, from, to) => {
    if (from === to) return;
    if (!isSuperAdmin() && (AR_COMMUNICATION_ADMIN_ONLY.includes(to) || AR_COMMUNICATION_ADMIN_ONLY.includes(from))) {
      setSnackbar({ open: true, message: 'Only Super Admins can set Invoiced, Pushed or Pulled', severity: 'info' });
      return;
    }
    const byName = `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || currentUser?.email || 'Unknown user';
    const entry = {
      from,
      to,
      byUid: currentUser?.uid || '',
      byName,
      byColor: userProfile?.color || fallbackColorForUid(currentUser?.uid),
      at: new Date().toISOString(),
    };
    try {
      await setDoc(doc(db, 'ar_communications', getCommunicationDocId(line)), {
        contractId: line.contractId || '',
        lineId: String(line.lineId ?? ''),
        clientId: line.clientId || '',
        status: to,
        updatedAt: serverTimestamp(),
        updatedByUid: entry.byUid,
        history: arrayUnion(entry),
      }, { merge: true });
    } catch (e) {
      console.error('Error updating communication status:', e);
      setSnackbar({ open: true, message: 'Failed to update communication status', severity: 'error' });
    }
  };

  const setInvoiceEdit = (line, field, value) => {
    const rowKey = getReceivableRowKey(line);
    setInvoiceEdits((prev) => ({
      ...prev,
      [rowKey]: { ...(prev[rowKey] || {}), [field]: value }
    }));
  };

  const saveInvoiceMeta = async (line) => {
    if (!isSuperAdmin()) { setSnackbar({ open: true, message: 'Read-only for Partners', severity: 'info' }); return; }
    try {
      const ct = contracts.find((c) => c.id === line.contractId);
      if (!ct) return;
      const logs = Array.isArray(ct.milestoneLogs) ? ct.milestoneLogs.map((m) => ({ ...m })) : [];
      const idx = logs.findIndex((m) => m.id === line.lineId);
      if (idx < 0) return;
      const rowKey = getReceivableRowKey(line);
      const pending = invoiceEdits[rowKey] || {};
      const nextInvoiceId = (pending.invoiceId ?? '').toString().trim();
      const nextInvoiceUrl = (pending.invoiceUrl ?? '').toString().trim();
      if (!nextInvoiceId && !nextInvoiceUrl) { setSnackbar({ open: true, message: 'Enter invoice number or URL', severity: 'info' }); return; }
      logs[idx].invoiceId = nextInvoiceId || logs[idx].invoiceId || '';
      logs[idx].invoiceUrl = nextInvoiceUrl || logs[idx].invoiceUrl || '';
      await updateDoc(doc(db, 'contracts', ct.id), { milestoneLogs: logs });
      setSnackbar({ open: true, message: 'Invoice details saved', severity: 'success' });
      setInvoiceEdits((prev) => { const { [rowKey]: _removed, ...rest } = prev; return rest; });
      await loadBase();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save invoice details', severity: 'error' });
    }
  };

  const recordInvoiceDeposit = async (line) => {
    try {
      if (!isSuperAdmin()) {
        setSnackbar({ open: true, message: 'Only Super Admin can record deposits', severity: 'info' });
        return;
      }
      if (line.status !== 'Invoiced') {
        setSnackbar({ open: true, message: 'Deposits can only be recorded for Invoiced receivables', severity: 'warning' });
        return;
      }
      if (line.autoDepositBankLineId) {
        setSnackbar({ open: true, message: 'A deposit has already been recorded for this invoice', severity: 'info' });
        return;
      }
      if (!String(line.invoiceId || '').trim()) {
        setSnackbar({ open: true, message: 'Save an invoice number before recording the deposit', severity: 'warning' });
        return;
      }
      if (!ucenaCompany) {
        setSnackbar({ open: true, message: 'An active UCENA company entity is required to record the deposit fee', severity: 'error' });
        return;
      }

      const amountUsd = Number(line.outstanding || 0);
      if (!(amountUsd > 0)) {
        setSnackbar({ open: true, message: 'This invoice has no outstanding amount to deposit', severity: 'info' });
        return;
      }
      const rawMethod = String(line.paymentMethod || '').toLowerCase();
      const paymentMethod = rawMethod === 'check'
        ? 'check_deposit'
        : (rawMethod === 'credit card' || rawMethod === 'creditcard' || rawMethod === 'credit_card')
          ? 'credit_card_deposit'
          : 'direct_deposit';

      setDepositingRowKey(getReceivableRowKey(line));
      const result = await createAutoClientDeposit({
        date: new Date().toISOString().slice(0, 10),
        amountUsd,
        clientId: line.clientId,
        clientName: line.clientName,
        invoiceNumber: line.invoiceId,
        paymentMethod,
        ucenaCompanyId: ucenaCompany.id,
        financeConfig,
        createdBy: currentUser?.uid,
        contractId: line.contractId,
        milestoneId: line.lineId,
      });
      setClientBalances((previous) => ({
        ...previous,
        [line.clientId]: result.availableBalance !== null && Number.isFinite(Number(result.availableBalance))
          ? Number(result.availableBalance)
          : Number(previous[line.clientId] || 0) + Number(result.amountUsd || 0),
      }));
      setSnackbar({
        open: true,
        message: `Deposit recorded and ${line.clientName} credited ${formatCurrency(result.amountUsd)}. UCENA fee: ${formatCurrency(result.feeUsd)}.`,
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || 'Failed to record deposit', severity: 'error' });
    } finally {
      setDepositingRowKey('');
    }
  };

  const getNiceUrlText = (url) => {
    try {
      const u = new URL(url);
      return u.hostname.replace(/^www\./, '');
    } catch { return 'Open'; }
  };

  return (
    <Container maxWidth={false}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ARIcon sx={{ fontSize: 24, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight="bold">Accounts Receivable</Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={clients}
              getOptionLabel={(c)=>c?.clientName || ''}
              isOptionEqualToValue={(o, v)=>o?.id === v?.id}
              value={clients.find(c=>c.id===filters.clientId) || null}
              onChange={(e,v)=>setFilters({ ...filters, clientId: v ? v.id : '' })}
              renderInput={(params)=> (
                <TextField
                  {...params}
                  label="Client"
                  placeholder="Select client"
                  sx={{ '& .MuiInputBase-root': { height: 44 }, '& .MuiInputBase-input': { py: 1.25 } }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              size="small"
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e)=>setFilters({ ...filters, status: e.target.value })}
              sx={{ '& .MuiInputBase-root': { height: 44 }, '& .MuiInputBase-input': { py: 1.25 } }}
            >
              <MenuItem value="">Both</MenuItem>
              <MenuItem value="Invoiced">Invoiced</MenuItem>
              <MenuItem value="Partial">Partial</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              size="small"
              fullWidth
              label="Overdue"
              value={filters.overdue}
              onChange={(e)=>setFilters({ ...filters, overdue: e.target.value })}
              sx={{ '& .MuiInputBase-root': { height: 44 }, '& .MuiInputBase-input': { py: 1.25 } }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">Overdue Only</MenuItem>
              <MenuItem value="no">Exclude Overdue</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={contracts}
              getOptionLabel={(c)=>c?.title || c?.id || ''}
              isOptionEqualToValue={(o, v)=>o?.id === v?.id}
              value={contracts.find(c=>c.id===filters.contractId) || null}
              onChange={(e,v)=>setFilters({ ...filters, contractId: v ? v.id : '' })}
              renderInput={(params)=> (
                <TextField
                  {...params}
                  label="Contract"
                  placeholder="Select contract"
                  sx={{ '& .MuiInputBase-root': { height: 44 }, '& .MuiInputBase-input': { py: 1.25 } }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        groupedByClient.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">No outstanding receivables.</Typography>
          </Paper>
        ) : (
          <Paper sx={{ p: 1.5, display: 'flex', minHeight: 360 }}>
            <Box sx={{ borderRight: 1, borderColor: 'divider', minWidth: 190, maxWidth: 220 }}>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={selectedClientIndex}
                onChange={(e, v)=>setSelectedClientIndex(v)}
                sx={{ height: '100%', '& .MuiTab-root': { minHeight: 30, py: 0.75, px: 1.25 }, '& .MuiTabs-indicator': { width: 3 } }}
              >
                {groupedByClient.map((group) => (
                  <Tab
                    key={group.clientId}
                    label={(
                      <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{group.clientName}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>Available: {formatCurrency(getClientAvailableDisplay(group.clientId))}</Typography>
                      </Box>
                    )}
                  />
                ))}
              </Tabs>
            </Box>
            <Box sx={{ flex: 1, p: 1.5, overflowY: 'auto', maxHeight: '60vh' }}>
              {(() => {
                const group = groupedByClient[selectedClientIndex] || null;
                if (!group) return null;
                return (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" sx={{ '& td, & th': { py: 1, px: 1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Contract</TableCell>
                          <TableCell>Period</TableCell>
                          <TableCell>Finance</TableCell>
                          <TableCell>Communications</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Applied</TableCell>
                          <TableCell>Invoice</TableCell>
                          <TableCell>Deposit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.lines.map((l) => (
                          <TableRow key={getReceivableRowKey(l)}>
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                component={RouterLink}
                                to={{
                                  pathname: '/contracts',
                                  search: l.contractId ? `?openContractId=${encodeURIComponent(l.contractId)}` : '',
                                }}
                                state={{ openContractId: l.contractId }}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                              >
                                {l.contractTitle}
                              </Typography>
                            </TableCell>
                            <TableCell>{l.expectedDate || '—'}</TableCell>
                            <TableCell>
                              <TextField
                                select
                                size="small"
                                value={l.status}
                                onChange={(e)=>handleStatusChange(l, e.target.value)}
                                disabled={!isSuperAdmin()}
                                sx={{ '& .MuiInputBase-root': { height: 40 }, '& .MuiInputBase-input': { py: 1 } }}
                              >
                                <MenuItem value="Scheduled" disabled={l.status === 'Invoiced' || l.status === 'Paid'}>Scheduled</MenuItem>
                                <MenuItem value="Invoiced">Invoiced</MenuItem>
                              <MenuItem value="Partial">Partial</MenuItem>
                                <MenuItem value="Paid">Paid</MenuItem>
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <ARCommunicationCell
                                record={communications[getCommunicationDocId(l)]}
                                isAdmin={isSuperAdmin()}
                                onChange={(from, to) => updateCommunicationStatus(l, from, to)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography variant="body2" fontWeight="bold">{l.paymentMethodCode}</Typography>
                                <Typography variant="body2">{formatCurrency(l.status==='Partial' ? (l.actualAmount || l.expectedAmount) : l.expectedAmount)}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const lineAmount = l.status==='Partial' ? (l.actualAmount || l.expectedAmount) : l.expectedAmount;
                                const rowKey = getReceivableRowKey(l);
                                const draftVal = appliedDrafts[rowKey];
                                const currentApplied = Number((draftVal !== undefined && draftVal !== '') ? Number(draftVal || 0) : Number(l.appliedAmount || 0));
                                const clientAvailEffective = Number(getClientAvailableDisplay(l.clientId) || 0) + currentApplied;
                                const allowedMax = Math.min(Number(lineAmount || 0), clientAvailEffective || Number(lineAmount || 0));
                                return (
                                  <TextField
                                    size="small"
                                    type="text"
                                    inputProps={{ inputMode: 'decimal', pattern: '^\\d*\\.?\\d*$', max: allowedMax }}
                                    value={(appliedDrafts[rowKey] !== undefined ? appliedDrafts[rowKey] : Number(l.appliedAmount || 0).toFixed(2))}
                                    onChange={(e)=>{
                                      const raw = e.target.value || '';
                                      const cleaned = raw.replace(/[^0-9.]/g, '');
                                      const parts = cleaned.split('.');
                                      const normalized = parts.length <= 2 ? cleaned : parts[0] + '.' + parts.slice(1).join('');
                                      setAppliedDrafts((prev)=>({ ...prev, [rowKey]: normalized }));
                                    }}
                                    onBlur={(e)=>updateAppliedAmount(l, Number((appliedDrafts[rowKey] ?? e.target.value) || 0))}
                                    onKeyDown={(e)=>{ if (e.key === 'Enter') { e.preventDefault(); updateAppliedAmount(l, Number(e.currentTarget.value || 0)); } }}
                                    disabled={!isSuperAdmin()}
                                    sx={{ maxWidth: 120, '& .MuiInputBase-root': { height: 40 }, '& .MuiInputBase-input': { py: 1 } }}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {l.invoiceId || l.invoiceUrl ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {l.invoiceUrl && l.invoiceId ? (
                                    <a href={l.invoiceUrl} target="_blank" rel="noreferrer">{l.invoiceId}</a>
                                  ) : l.invoiceId ? (
                                    <Typography variant="body2" fontWeight="bold">{l.invoiceId}</Typography>
                                  ) : (
                                    <a href={l.invoiceUrl} target="_blank" rel="noreferrer">{getNiceUrlText(l.invoiceUrl)}</a>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TextField
                                    size="small"
                                    label="Invoice #"
                                    value={(invoiceEdits[getReceivableRowKey(l)]?.invoiceId) || ''}
                                    onChange={(e)=>setInvoiceEdit(l, 'invoiceId', e.target.value)}
                                    sx={{ width: 110, '& .MuiInputBase-root': { height: 40 }, '& .MuiInputBase-input': { py: 1 } }}
                                    disabled={!isSuperAdmin()}
                                  />
                                  <TextField
                                    size="small"
                                    label="Invoice URL"
                                    value={(invoiceEdits[getReceivableRowKey(l)]?.invoiceUrl) || ''}
                                    onChange={(e)=>setInvoiceEdit(l, 'invoiceUrl', e.target.value)}
                                    sx={{ width: 100, '& .MuiInputBase-root': { height: 40 }, '& .MuiInputBase-input': { py: 1 } }}
                                    disabled={!isSuperAdmin()}
                                  />
                                  <Button size="small" variant="contained" onClick={()=>saveInvoiceMeta(l)} disabled={!isSuperAdmin()}>Save</Button>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {l.status === 'Invoiced' && (
                                l.autoDepositBankLineId ? (
                                  <Chip size="small" label="Deposited" color="success" variant="outlined" />
                                ) : (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => recordInvoiceDeposit(l)}
                                    disabled={
                                      !isSuperAdmin()
                                      || depositingRowKey === getReceivableRowKey(l)
                                      || !l.invoiceId
                                      || Number(l.outstanding || 0) <= 0
                                    }
                                    title={!l.invoiceId ? 'Save an invoice number first' : 'Record this invoice payment in Bank and credit the client'}
                                  >
                                    {depositingRowKey === getReceivableRowKey(l) ? <CircularProgress size={16} color="inherit" /> : 'Record Deposit'}
                                  </Button>
                                )
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </Box>
          </Paper>
        )
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
